'use client';

import { useMemo } from 'react';

import type { FundingKey, FundingModel } from '@bytel/leviator';
import {
    DirectionEnum,
    FlexBox,
    FlexItem,
    Text,
    TextLevels,
    TypographyBold,
    TypographyColor,
} from '@bytel/trilogy-react';

import { FundingInstallmentItem } from './funding-installment-item';

export interface FundingKeySelectorProps {
    value: FundingKey | '';
    options: FundingModel[];
    onChange: (key: FundingKey) => void;
    error?: string;
    isHidden?: boolean;
    hasOdr: boolean;
    odrRefund: number;
    phonePriceWithOdrDeducted: number;
}

export function FundingKeySelector({
    value,
    options,
    onChange,
    error,
    isHidden = false,
    hasOdr,
    odrRefund,
    phonePriceWithOdrDeducted,
}: Readonly<FundingKeySelectorProps>) {
    const sortedOptions = useMemo(
        () => [...options].sort((a, b) => a.initialDeposit - b.initialDeposit || a.monthlyAmount - b.monthlyAmount),
        [options],
    );

    if (isHidden) {
        return null;
    }

    return (
        <FlexItem>
            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                Avec quelle formule&nbsp;?
            </Text>
            <FlexBox direction={DirectionEnum.COLUMN} gap={2} mobile>
                {sortedOptions.map((funding) => (
                    <FundingInstallmentItem
                        key={funding.key}
                        funding={funding}
                        isSelected={value === funding.key}
                        onSelect={() => onChange(funding.key)}
                        hasOdr={hasOdr}
                        odrRefund={odrRefund}
                        phonePriceWithOdrDeducted={phonePriceWithOdrDeducted}
                    />
                ))}
            </FlexBox>
            {error && (
                <Text level={TextLevels.THREE} typo={TypographyColor.TEXT_ERROR}>
                    {error}
                </Text>
            )}
        </FlexItem>
    );
}
